/** Petits outils SVG partagés par les deux graphiques. */
import { useEffect, useRef, useState } from "react";
import type { Pt } from "./series";

export const COULEURS = {
  scenario: "#2a78d6",
  reference: "#1baf7a",
  voiture: "#eb6834",
  terrain: "#8a8a86",
  limite: "#7a7a76",
  grille: "#e3e1dc",
  encre: "#141414",
  gris: "#6b6b67",
  accent: "#ffb703",
};

/** Largeur du conteneur, suivie par ResizeObserver. */
export function useLargeur<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => setW(Math.floor(entries[0].contentRect.width)));
    ro.observe(el);
    setW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

export function echelle(d0: number, d1: number, r0: number, r1: number): (v: number) => number {
  const k = d1 === d0 ? 0 : (r1 - r0) / (d1 - d0);
  return (v) => r0 + (v - d0) * k;
}

export function chemin(pts: Pt[], sx: (x: number) => number, sy: (y: number) => number): string {
  if (pts.length === 0) return "";
  let d = `M${sx(pts[0][0]).toFixed(1)},${sy(pts[0][1]).toFixed(1)}`;
  for (let k = 1; k < pts.length; k++) d += `L${sx(pts[k][0]).toFixed(1)},${sy(pts[k][1]).toFixed(1)}`;
  return d;
}

export function cheminXY(xs: number[], ys: number[], sx: (x: number) => number, sy: (y: number) => number): string {
  if (xs.length === 0) return "";
  let d = `M${sx(xs[0]).toFixed(1)},${sy(ys[0]).toFixed(1)}`;
  for (let k = 1; k < xs.length; k++) d += `L${sx(xs[k]).toFixed(1)},${sy(ys[k]).toFixed(1)}`;
  return d;
}

/** Aire fermée sur une ligne de base. */
export function aire(xs: number[], ys: number[], sx: (x: number) => number, sy: (y: number) => number, base: number): string {
  if (xs.length === 0) return "";
  return `${cheminXY(xs, ys, sx, sy)}L${sx(xs[xs.length - 1]).toFixed(1)},${sy(base).toFixed(1)}L${sx(xs[0]).toFixed(1)},${sy(base).toFixed(1)}Z`;
}

export interface VillePlacee {
  name: string;
  x: number;
  anchor: "start" | "middle" | "end";
  /** décalage vertical (px) quand deux libellés se serrent */
  dy: number;
}

/** Libellés des villes-repères : ancrés aux bords, décalés d'une ligne quand leurs emprises se touchent. */
export function placerVilles(villes: { name: string; x: number }[], x0: number, x1: number, pxParCar = 6.2): VillePlacee[] {
  const out: VillePlacee[] = [];
  let prevFin = -Infinity;
  let prevDy = 12;
  for (const v of villes) {
    const w = v.name.length * pxParCar;
    let anchor: VillePlacee["anchor"] = "middle";
    let debut = v.x - w / 2;
    if (debut < x0) {
      anchor = "start";
      debut = v.x;
    } else if (v.x + w / 2 > x1) {
      anchor = "end";
      debut = v.x - w;
    }
    const dy = debut < prevFin + 6 ? (prevDy === 0 ? 12 : 0) : 0;
    out.push({ ...v, anchor, dy });
    prevFin = debut + w;
    prevDy = dy;
  }
  return out;
}
